import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  SafeAreaView,
} from '../utils/webComponents';
import { useNavigation, useRoute } from '../utils/navigation';
import { Style, Edit } from '@mui/icons-material';
import { useFlashcards } from '../context/FlashcardContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';

const EditFlashcardScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { cardId } = route.params || {};
  const { flashcards, updateFlashcard } = useFlashcards();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [front, setFront] = useState('');
  const [back, setBack] = useState('');
  const [loading, setLoading] = useState(false);

  // Find the flashcard
  const flashcard = flashcards.find(card => card.id === cardId);

  // Load flashcard data
  useEffect(() => {
    if (flashcard) {
      setFront(flashcard.front || '');
      setBack(flashcard.back || '');
    }
  }, [flashcard]);

  // If flashcard not found, show error and navigate back
  useEffect(() => {
    if (!flashcard && flashcards.length > 0) {
      setTimeout(() => {
        navigation.goBack();
      }, 2000);
    }
  }, [flashcard, flashcards, navigation]);

  const handleUpdateFlashcard = async () => {
    if (!front.trim() || !back.trim()) {
      return;
    }

    if (!cardId) {
      return;
    }

    setLoading(true);

    try {
      updateFlashcard({
        id: cardId,
        front: front.trim(),
        back: back.trim(),
      });

      // Replace current screen with set detail screen (removes edit screen from history)
      if (flashcard?.setId) {
        navigation.replace('SetDetail', { setId: flashcard.setId });
      } else {
        navigation.goBack();
      }
    } catch (error) {
      console.error('Error updating flashcard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!flashcard) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
        <View style={styles.loadingContainer}>
          <Text style={[styles.loadingText, { color: theme.text }]}>
            {t('common.loading')}
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={styles.contentContainer}>
          <View style={[styles.content, { backgroundColor: theme.surface, borderColor: theme.border }]}>
            <View style={styles.iconContainer}>
              <View style={[styles.iconCircle, { backgroundColor: theme.primaryLight }]}>
                <Style sx={{ fontSize: 48, color: theme.primary }} />
              </View>
            </View>
            
            <Text style={[styles.title, { color: theme.text }]}>{t('flashcard.editFlashcard')}</Text>

            <View style={styles.form}>
              <View style={styles.inputContainer}>
                <Text style={[styles.inputLabel, { color: theme.text, marginBottom: 20, lineHeight: '1.8' }]}>{t('flashcard.frontQuestion')}</Text>
                <TextInput
                  style={[styles.textInput, { backgroundColor: theme.inputBackground || theme.surface, color: theme.text, borderColor: theme.border, lineHeight: '1.8' }]}
                  value={front}
                  onChangeText={setFront}
                  placeholder={t('flashcard.frontPlaceholder')}
                  placeholderTextColor={theme.textSecondary}
                  multiline
                  numberOfLines={3}
                />
              </View>

              <View style={styles.inputContainer}>
                <Text style={[styles.inputLabel, { color: theme.text, marginBottom: 20, lineHeight: '1.8' }]}>{t('flashcard.backAnswer')}</Text>
                <TextInput
                  style={[styles.textInput, styles.textArea, { backgroundColor: theme.inputBackground || theme.surface, color: theme.text, borderColor: theme.border, lineHeight: '1.8' }]}
                  value={back}
                  onChangeText={setBack}
                  placeholder={t('flashcard.backPlaceholder')}
                  placeholderTextColor={theme.textSecondary}
                  multiline
                  numberOfLines={4}
                />
              </View>
            </View>

            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[styles.button, styles.cancelButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
                onPress={() => navigation.goBack()}
              >
                <Text style={[styles.cancelButtonText, { color: theme.text }]}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.button, styles.saveButton, { backgroundColor: theme.primary }, (loading || !front.trim() || !back.trim()) && styles.disabledButton]}
                onPress={handleUpdateFlashcard}
                disabled={loading || !front.trim() || !back.trim()}
              >
                <Edit sx={{ fontSize: 20, color: 'white' }} />
                <Text style={[styles.saveButtonText, { color: 'white' }]}>
                  {loading ? t('common.loading') : t('common.save')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    fontSize: 18,
  },
  scrollView: {
    flex: 1,
    display: 'flex',
  },
  scrollContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    minHeight: '100vh',
  },
  contentContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: 600,
  },
  content: {
    width: '100%',
    maxWidth: 600,
    padding: 32,
    borderRadius: 16,
    borderWidth: 1,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    margin: '0 auto',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 32,
  },
  iconCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 32,
  },
  form: {
    marginBottom: 32,
  },
  inputContainer: {
    marginBottom: 32,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 1.8,
  },
  textInput: {
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    borderWidth: 1,
    width: '100%',
    transition: 'border-color 0.2s',
    lineHeight: 1.8,
  },
  textArea: {
    minHeight: 100,
    resize: 'vertical',
    lineHeight: 1.8,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
  },
  button: {
    flex: 1,
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s, opacity 0.2s',
    cursor: 'pointer',
    gap: 8,
  },
  cancelButton: {
    borderWidth: 1,
  },
  saveButton: {
    borderWidth: 0,
  },
  disabledButton: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default EditFlashcardScreen;
